import { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router';
import { FormProvider, useForm, type FieldErrors } from 'react-hook-form';
import { adaptQuestions, getApplicationGateReasonCopy } from '@tempmee/awards';
import {
  Button,
  Column,
  Divider,
  FormField,
  Row,
  TextField,
  Toast,
  Typography,
} from '@tempmee/react';
import { OutlineArrowLeftIcon } from '@tempmee/react-icons';
import {
  classifyBidSubmitFailure,
  useGetApplicationQuestions,
  useSubmitBidApplication,
} from '~/domain/BestInDental';
import { FixedWidthScreen } from '~/commonComponents/FixedWidthScreen';
import { useViewedOfficeScope } from '~/hooks/useViewedOfficeScope';
import { routes } from '~/routes';
import {
  getViewedOfficeId,
  SELECTED_OFFICE_SCOPE,
  toViewedOfficePath,
} from '~/utils/viewedOfficeScope';
import {
  BID_TOAST_AUTO_HIDE_MS,
  BidErrorState,
  BidFieldError,
  BidFormFieldRenderer,
  BidLoadingState,
  BidSubmitError,
  BidTermsCheckbox,
  buildBidAnswers,
  buildBidDefaultValues,
  buildBidFormResolver,
  MAILING_ADDRESS_HELPER_TEXT,
  validateBidTermsField,
} from '../shared';

// Server trims and caps mailingAddress at 300 characters (awards-service#25).
const MAILING_ADDRESS_MAX_LENGTH = 300;

// mailingAddress and termsAccepted are top-level submit fields, not questions
// in the schema — validate them so the form can never submit a blank address
// or attest to terms the user never checked (the server 400s on both, and
// attesting unchecked consent is a compliance bug).
const validateApplicationExtras = (
  values: Record<string, unknown>,
): FieldErrors<Record<string, unknown>> => {
  const errors: FieldErrors<Record<string, unknown>> = {
    ...validateBidTermsField('termsAccepted', values),
  };
  const mailingAddress = String(values.mailingAddress ?? '').trim();
  if (!mailingAddress) {
    errors.mailingAddress = { type: 'required', message: 'Mailing address is required.' };
  } else if (mailingAddress.length > MAILING_ADDRESS_MAX_LENGTH) {
    errors.mailingAddress = {
      type: 'maxLength',
      message: `Mailing address must be ${MAILING_ADDRESS_MAX_LENGTH} characters or fewer.`,
    };
  }
  return errors;
};

export const BidApplicationForm = () => {
  const { cycleId } = useParams<{ cycleId: string }>();
  const navigate = useNavigate();
  // Started from a location's page, the application is that location's.
  const scope = useViewedOfficeScope() ?? SELECTED_OFFICE_SCOPE;
  const officeId = getViewedOfficeId(scope);
  const mutation = useSubmitBidApplication(officeId);
  const submitFailure = mutation.isError ? classifyBidSubmitFailure(mutation.error) : undefined;
  const {
    questions,
    eligible,
    ineligibleReason,
    isLoading: isLoadingQuestions,
    isError: isQuestionsError,
    refetch: refetchQuestions,
  } = useGetApplicationQuestions(cycleId, officeId);

  const fields = useMemo(() => adaptQuestions(questions), [questions]);
  const resolver = useMemo(
    () =>
      fields.length
        ? buildBidFormResolver('office-application', fields, validateApplicationExtras)
        : undefined,
    [fields],
  );

  const form = useForm({
    resolver,
    defaultValues: fields.length
      ? buildBidDefaultValues(fields, { mailingAddress: '', termsAccepted: '' })
      : {},
  });

  const onSubmit = (values: Record<string, unknown>) => {
    if (!cycleId) return;
    mutation.mutate({
      cycleId,
      answers: buildBidAnswers(values, ['mailingAddress', 'termsAccepted']),
      mailingAddress: String(values.mailingAddress ?? '').trim(),
      termsAccepted: values.termsAccepted === 'true',
    });
  };

  const handleRetry = () => mutation.reset();

  const goToBidPage = () => navigate(toViewedOfficePath(routes.bestInDental, scope));

  if (!cycleId) {
    return (
      <Column data-cy="bid-application-form-invalid" gap={4} p={6}>
        <Typography>This application link is invalid.</Typography>
        <Button onClick={goToBidPage}>Back to Best in Dental</Button>
      </Column>
    );
  }

  if (isLoadingQuestions) {
    return <BidLoadingState data-cy="bid-application-form-loading" />;
  }

  if (isQuestionsError) {
    return (
      <BidErrorState
        data-cy="bid-application-form-error"
        message="Something went wrong loading the application form."
        onRetry={() => refetchQuestions()}
      />
    );
  }

  if (eligible === false) {
    return (
      <Column data-cy="bid-application-form-ineligible" gap={4} p={6}>
        <Typography>{getApplicationGateReasonCopy(ineligibleReason)}</Typography>
        <Row gap={2}>
          {ineligibleReason === 'unknown-identity' && (
            <Button
              variant="outlined"
              onClick={() => refetchQuestions()}
              data-cy="bid-application-ineligible-retry"
            >
              Try again
            </Button>
          )}
          <Button onClick={goToBidPage}>Back to Best in Dental</Button>
        </Row>
      </Column>
    );
  }

  return (
    <FixedWidthScreen data-cy="bid-application-form">
      <Row>
        <Button
          type="button"
          variant="text"
          startIcon={<OutlineArrowLeftIcon />}
          onClick={goToBidPage}
          data-cy="bid-application-back-button"
        >
          Back
        </Button>
      </Row>

      <Typography variant="h4" textAlign="center">
        Best in Dental
      </Typography>

      <Column gap={2}>
        <Typography variant="h6">Application</Typography>
        <Divider />
      </Column>

      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <Column gap={4}>
            {fields.map((field) => (
              <BidFormFieldRenderer key={field.name} field={field} />
            ))}

            <Column gap={1} data-cy="bid-field-mailingAddress">
              <FormField label="Mailing Address" htmlFor="mailingAddress" required>
                <TextField
                  id="mailingAddress"
                  helperText={MAILING_ADDRESS_HELPER_TEXT}
                  error={Boolean(form.formState.errors.mailingAddress)}
                  {...form.register('mailingAddress')}
                />
              </FormField>
              <BidFieldError
                name="mailingAddress"
                message={form.formState.errors.mailingAddress?.message as string | undefined}
              />
            </Column>

            <BidTermsCheckbox name="termsAccepted" dataCy="bid-application-terms" />

            {submitFailure && (
              <BidSubmitError
                data-cy="bid-application-form-submit-error"
                message={
                  submitFailure.kind === 'rejected'
                    ? submitFailure.message
                    : 'Something went wrong submitting your application. Please try again.'
                }
                onRetry={submitFailure.kind === 'failed' ? handleRetry : undefined}
              />
            )}

            <Row justify="end">
              <Button
                type="submit"
                data-cy="bid-application-submit-button"
                disabled={mutation.isPending || submitFailure?.kind === 'failed'}
              >
                Submit application
              </Button>
            </Row>
          </Column>
        </form>
      </FormProvider>

      <Toast
        open={mutation.isSuccess}
        severity="success"
        message="Application sent."
        autoHideDuration={BID_TOAST_AUTO_HIDE_MS}
        onClose={goToBidPage}
      />
    </FixedWidthScreen>
  );
};
